import { useId, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type { DocumentQuestion, QuestionMember } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import { FileLink, type CameFrom } from "./file-link";
import { relationPhrase } from "./family-words";

/**
 * The model's question about one proposed family, and Yes or No for each file
 * in it (`document-ai/0016` §11, §16).
 *
 * **One question per family** since 2026-09-26 (VER-08). Every file the model
 * was not sure of is a member, and a person answers each one on its own. Under
 * the members, the question shows what each file says about itself: when and
 * with which program it was written, its pages, its signatures, and what it
 * shares with the others. These are hints for the person, as they are for the
 * model; the code does not put two versions in order by them (VER-01).
 *
 * Shared by the file's own screen and the review after an import, so a person
 * reads the same question in the same words in both. **Presentational only:**
 * the caller owns the answer's mutation, its error and what it says once
 * answered. Answering re-reads the file, the member goes, and so does this box
 * when no member is left: anything said after that has to live outside it.
 */
export function PlacementQuestion({
  projectId,
  question,
  self = null,
  busy,
  onAnswer,
  from,
  heading = "Needs a decision",
  lead,
}: {
  projectId: string;
  question: DocumentQuestion;
  /** The file whose screen this is: its member reads "this file". Null on the review. */
  self?: string | null;
  busy: boolean;
  onAnswer: (member: QuestionMember, yes: boolean) => void;
  /** Carried on the links to the other files, so their back links return here. */
  from?: CameFrom;
  heading?: string;
  /** What the question is about, when the page does not already say. */
  lead?: ReactNode;
}) {
  const ids = useId();
  const several = question.members.length > 1;
  const files = question.files.filter((file) => file.facts.length > 0);
  return (
    <div className="rounded-card-lg border-[1.5px] border-warn/40 bg-warn-bg px-4 py-3">
      <p className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-warn-text uppercase">
        {heading}
      </p>
      {lead}
      {several && (
        <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
          {question.name
            ? `The model thinks that these ${question.members.length} files are versions of “${question.name}”, but it was not sure.`
            : `The model thinks that these ${question.members.length} files are versions of one document, but it was not sure.`}{" "}
          Each file stays on its own until someone answers for it.
        </p>
      )}
      <ul className="flex flex-col gap-3">
        {question.members.map((member, at) => (
          <li
            key={member.document.id}
            role="group"
            aria-labelledby={`${ids}-member-${at}`}
            className={several ? "mt-2 border-t border-warn/30 pt-2" : undefined}
          >
            <p id={`${ids}-member-${at}`} className="mt-1 text-[0.92rem] font-semibold text-ink">
              Is{" "}
              {member.document.id === self ? (
                "this file"
              ) : (
                <>
                  “
                  <FileLink projectId={projectId} id={member.document.id} from={from}>
                    {member.document.name}
                  </FileLink>
                  ”
                </>
              )}{" "}
              {relationPhrase(member.relation)} “
              <FileLink projectId={projectId} id={member.other.id} from={from}>
                {member.other.name}
              </FileLink>
              ”?
            </p>
            {!several && (
              <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
                The model wasn’t sure, so this file stays on its own until someone answers.
              </p>
            )}
            <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
              The model’s reason: {member.reason}
            </p>
            {member.why.length > 0 && (
              <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
                Why it asks: {member.why.join(" ")}
              </p>
            )}
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                className="hover:bg-brand-deep"
                disabled={busy}
                onClick={() => onAnswer(member, true)}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                Yes
              </Button>
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => onAnswer(member, false)}>
                No
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {files.length > 0 && (
        <div className="mt-3 border-t border-warn/30 pt-2">
          <p id={`${ids}-facts`} className="text-[0.78rem] font-semibold text-ink-slate">
            What the files show
          </p>
          <p className="mt-0.5 text-[0.78rem] leading-[1.5] text-muted">
            These come from the files themselves. A time can change when a file is copied, so they are hints.
          </p>
          <ul aria-labelledby={`${ids}-facts`} className="mt-1 flex flex-col gap-1.5">
            {files.map((file) => (
              <li key={file.document.id} className="text-[0.8rem] leading-[1.5] text-ink-slate">
                <FileLink projectId={projectId} id={file.document.id} from={from}>
                  {file.document.name}
                </FileLink>
                <ul className="ml-3 list-disc pl-3 text-muted">
                  {file.facts.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
